import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  checkinFaceUser,
  clearAllGuestKioskState,
  getFaceUserProfile,
  persistFaceProfileSnapshot,
  readFaceCaptureDataUrl,
  readFacePending,
  readFaceProfileSnapshot,
  resolveFaceOrganizationId,
  resolveFaceVisitOrganizationId,
  submitPassportReturningCheckin,
  passportDuplicateVisitMessage,
} from '../utils/faceIdentityApi';
import {
  clearPassportIdleVideoDelay,
  disablePassportKioskScanAfterCapture,
  schedulePassportIdleVideoDelay,
} from '../utils/passportKioskApi';
import {
  commitPassportWelcomeProfileSaved,
  fetchActiveVisit,
} from '../../salon-admin/utils/checkinQueueApi';
import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import FaceIdentityBrandLogo from '../components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../components/FaceIdentityFlowNav';
import { recentPreviousServices } from '../utils/previousServicesSummary';
import { useFaceIdentityOrgLogo } from '../hooks/useFaceIdentityOrgLogo';
import { buildKioskNavState } from '../utils/kioskNavState';
import {
  faceIdentityRoute,
  isPassportFlow,
  isPassportCheckinBoardFlow,
  isGuestCheckInFlow,
} from '../utils/faceIdentityRoutes';
import { compressFaceThumbnail, resolveFacePortraitUrl } from '../utils/facePortrait';
import { clearGuestFlowAuth, readGuestFlowAuth } from '../../shared/guestFlowAuth';
import {
  isPresentationNavContext,
  resolvePresentationAwareHomePath,
} from '../../presentation/presentationSession';
import GuestCheckInCompleteModal from '../components/GuestCheckInCompleteModal';
import ConfirmFaceMatchPicker from '../components/ConfirmFaceMatchPicker';
import { markSalonFaceCheckedIn, buildSalonVisitReference, setActiveCheckinSession } from '../../salon/utils/salonFaceSession';
import { getFranchiseOrganizationId, franchiseAuthHeaders } from '../../shared/franchiseFlowAuth';
import { SALON_GUEST_INTENT_PATH } from '../../salon/config/salonApp';
import { VISIT_INTENT_SERVICE } from '../../salon/utils/visitIntent';
import { handoffFromCheckin } from '../../salon/utils/visitHandoff';
import '../styles/FaceIdentityLuxTheme.css';
import './FaceIdentityWelcomePage.css';
import '../styles/FaceIdentityScrollShell.css';
import { useFaceIdentityScrollShell } from '../hooks/useFaceIdentityScrollShell';
import { usePassportKioskScanGate } from '../hooks/usePassportKioskScanGate';

function formatVisitAt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_) {
    return iso;
  }
}

function formatTodayPill(date = new Date()) {
  try {
    const day = date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `Today, ${day} · ${time}`;
  } catch (_) {
    return date.toLocaleString();
  }
}

const IconClose = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M9 9l6 6M15 9l-6 6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconPin = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 3.5V7M16 3.5V7M3.5 10h17" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconClock = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 7.5V12l3 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const FaceIdentityWelcomePage = () => {
  useFaceIdentityScrollShell();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const navigate = useNavigate();
  const location = useLocation();
  const isPassport = isPassportCheckinBoardFlow(location.pathname);
  const isPassportKiosk = isPassportFlow(location.pathname);
  // Path only — a leftover guest JWT must not turn the Passport kiosk into guest check-in.
  const isGuestPhone = isGuestCheckInFlow(location.pathname);
  usePassportKioskScanGate(isPassportKiosk);
  const presentationFromHub = isPresentationNavContext(location.state);
  const homePath = resolvePresentationAwareHomePath(location.pathname, location.state);
  const pending = readFacePending();
  const stored = readFaceProfileSnapshot();

  const user = location.state?.user || stored?.user || pending?.user || null;
  const [liveUser, setLiveUser] = useState(() => user);
  const [liveProfile, setLiveProfile] = useState(
    () => location.state?.profile || stored?.profile || user?.profile || pending?.profile || null,
  );
  const [deskProfileStamp, setDeskProfileStamp] = useState('');

  useEffect(() => {
    if (user) setLiveUser(user);
  }, [user]);
  const profile = liveProfile;
  const confidence = location.state?.confidence ?? stored?.confidence ?? pending?.confidence ?? null;
  const isNewMember = Boolean(location.state?.isNewMember || stored?.isNewMember || pending?.isNewMember);
  const [portraitUrl, setPortraitUrl] = useState(
    () =>
      resolveFacePortraitUrl(
        location.state,
        stored,
        pending,
        location.state?.user,
        stored?.user,
        pending?.user,
      ) || readFaceCaptureDataUrl() || '',
  );
  const captureUrl = portraitUrl;
  const token = location.state?.token || null;
  const deviceInfo = location.state?.deviceInfo || null;
  const passportCheckinId = location.state?.checkinId || null;
  const passportDuplicateVisit = Boolean(location.state?.passportDuplicateVisit);
  const [passportKioskDismissed, setPassportKioskDismissed] = useState(
    () => Boolean(location.state?.passportKioskDismissed),
  );
  const [completeModal, setCompleteModal] = useState({
    open: false,
    guestName: '',
    isNewMember: false,
    customerCode: '',
    services: [],
    warning: '',
    franchiseName: '',
  });
  const isGuestQr = isGuestPhone;

  const dismissPassportWelcome = useCallback(() => {
    const guestMeta = isGuestPhone ? readGuestFlowAuth() : null;
    const thankYouName =
      String(liveUser?.name || liveUser?.full_name || user?.name || user?.full_name || '').trim() ||
      'Guest';
    clearAllGuestKioskState();
    if (isPassportKiosk) {
      schedulePassportIdleVideoDelay(10000);
    }
    setPassportKioskDismissed(true);
    setLiveProfile(null);
    setPortraitUrl('');

    // Guest QR + Passport: thank-you only — never restart the camera / scan page.
    if (isGuestPhone) {
      setCompleteModal({
        open: true,
        guestName: thankYouName,
        isNewMember: false,
        customerCode: '',
        services: [],
        warning: '',
        franchiseName: guestMeta?.franchise_name || '',
      });
      return;
    }
  }, [isGuestPhone, isPassportKiosk, liveUser, user]);

  // Passport kiosk only: after desk confirm, leave thank-you and open the idle loop.
  // Guest QR stays on thank-you and must not restart the camera.
  const idleNavRef = useRef({ homePath, token, deviceInfo, state: location.state });
  idleNavRef.current = { homePath, token, deviceInfo, state: location.state };
  useEffect(() => {
    if (!isPassportKiosk || isGuestPhone || !passportKioskDismissed) return undefined;
    const timer = window.setTimeout(() => {
      const snap = idleNavRef.current;
      clearPassportIdleVideoDelay();
      void (async () => {
        await disablePassportKioskScanAfterCapture();
        navigate(snap.homePath, {
          replace: true,
          state: buildKioskNavState(snap.state, {
            token: snap.token,
            deviceInfo: snap.deviceInfo,
            mode: snap.state?.mode || 'frontdesk',
          }),
        });
      })();
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [isGuestPhone, isPassportKiosk, navigate, passportKioskDismissed]);

  useEffect(() => {
    if (!isPassport || passportKioskDismissed || !passportCheckinId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const visit = await fetchActiveVisit({
          checkinId: passportCheckinId,
          includeClosed: true,
        });
        if (cancelled || !visit) return;
        const status = String(visit.status || '').toLowerCase();
        const snap = visit.customer_snapshot || {};
        const deskAcked = Boolean(
          snap.passport_kiosk_acknowledged || snap.passportKioskAcknowledged,
        );
        const deskEditedAt = String(
          snap.passport_desk_profile_at || snap.passportDeskProfileAt || '',
        ).trim();
        if (deskEditedAt && deskEditedAt !== deskProfileStamp) {
          setDeskProfileStamp(deskEditedAt);
          const pendingProfile =
            snap.passport_desk_pending_profile || snap.passportDeskPendingProfile;
          const prefSource =
            (pendingProfile && typeof pendingProfile === 'object' && pendingProfile.preferences) ||
            snap.profile ||
            snap.preferences ||
            {};
          const nextName = String(
            pendingProfile?.name ||
              pendingProfile?.full_name ||
              snap.name ||
              snap.full_name ||
              '',
          ).trim();
          const nextMobile = String(pendingProfile?.mobile || snap.mobile || '').trim();
          const nextGender = String(pendingProfile?.gender || '').trim();
          if (nextName || nextMobile || nextGender) {
            setLiveUser((prev) => ({
              ...(prev || liveUser || {}),
              ...(nextName ? { name: nextName, full_name: nextName } : {}),
              ...(nextMobile ? { mobile: nextMobile } : {}),
              ...(nextGender ? { gender: nextGender } : {}),
            }));
          }
          if (prefSource && typeof prefSource === 'object') {
            setLiveProfile((prev) => ({
              ...(prev || {}),
              preferences: {
                ...(prev?.preferences || {}),
                ...prefSource,
              },
            }));
          }
        }
        // Front desk cancel (or confirm) clears Passport / Guest details.
        if (status === 'cancelled' || status === 'completed' || deskAcked) {
          dismissPassportWelcome();
        }
      } catch {
        /* ignore transient poll errors */
      }
    };
    poll();
    const timer = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    dismissPassportWelcome,
    deskProfileStamp,
    isPassport,
    liveUser,
    passportCheckinId,
    passportKioskDismissed,
    user?.id,
  ]);

  const [clockLabel, setClockLabel] = useState(() => formatTodayPill());

  useEffect(() => {
    const tick = () => setClockLabel(formatTodayPill());
    tick();
    const id = window.setInterval(tick, 30000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const faceUserId = user?.id;
    if (!faceUserId) return undefined;
    (async () => {
      try {
        const data = await getFaceUserProfile(faceUserId);
        const nextProfile = data?.profile || data?.user?.profile || null;
        if (!cancelled && nextProfile) {
          setLiveProfile((prev) => ({
            ...(prev || {}),
            ...nextProfile,
            history_summary: {
              ...(prev?.history_summary || {}),
              ...(nextProfile.history_summary || {}),
            },
          }));
        }
        const nextPortrait = resolveFacePortraitUrl(data?.user, data?.profile, data);
        if (!cancelled && nextPortrait) {
          setPortraitUrl((prev) => prev || nextPortrait);
        }
      } catch (_) {
        /* keep snapshot profile */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const summary = profile?.history_summary || {};
  const lastVisit = summary?.last_visit;
  const summaryLocation = lastVisit?.services_summary?.location;
  const lastVisitLocation =
    lastVisit?.franchise ||
    summary?.last_visit_location ||
    (summaryLocation && typeof summaryLocation === 'object' ? summaryLocation : null) ||
    null;
  const lastVisitCity =
    lastVisitLocation?.city || lastVisit?.city || summaryLocation?.city || '';
  const lastVisitArea =
    lastVisitLocation?.area || lastVisit?.area || summaryLocation?.area || '';
  const lastVisitCountry =
    lastVisitLocation?.country || lastVisit?.country || summaryLocation?.country || '';
  const lastVisitState =
    lastVisitLocation?.state || lastVisit?.state || summaryLocation?.state || '';
  const lastVisitAddress =
    lastVisitLocation?.address || lastVisit?.address || summaryLocation?.address || '';
  const lastVisitFranchiseName =
    lastVisitLocation?.name ||
    lastVisit?.franchise_name ||
    summaryLocation?.name ||
    summaryLocation?.franchise_name ||
    '';
  const hasLastVisitLocation = Boolean(
    lastVisitCity ||
      lastVisitArea ||
      lastVisitCountry ||
      lastVisitState ||
      lastVisitAddress ||
      lastVisitFranchiseName,
  );

  const preferences = useMemo(
    () => (profile?.preferences && typeof profile.preferences === 'object' ? profile.preferences : {}),
    [profile],
  );

  const [proceedNotice, setProceedNotice] = useState('');
  const [isContinuing, setIsContinuing] = useState(false);
  const [previousServices, setPreviousServices] = useState([]);

  // After desk confirm, Passport returns to the scan page so the idle loop can play.
  // Guest QR stays on thank-you and never restarts the camera.

  const confirmCandidatesForRedirect =
    location.state?.confirmCandidates || pending?.confirmCandidates || null;

  useEffect(() => {
    if (user) return;
    if (Array.isArray(confirmCandidatesForRedirect) && confirmCandidatesForRedirect.length > 0) {
      return;
    }
    if (isPassport && passportKioskDismissed) return;
    navigate(homePath, {
      replace: true,
      state: buildKioskNavState(location.state, {
        token,
        deviceInfo,
        next: location.state?.next || null,
        mode: location.state?.mode || 'frontdesk',
      }),
    });
  }, [
    user,
    confirmCandidatesForRedirect,
    isPassport,
    passportKioskDismissed,
    homePath,
    navigate,
    location.state,
    token,
    deviceInfo,
  ]);

  const lastTakenServices = useMemo(() => {
    const recent = recentPreviousServices(previousServices, 8);
    return recent
      .map((item) => {
        const name = String(item.service_name || item.name || '').trim();
        if (!name) return null;
        return { id: item.id || name, name, service_name: name };
      })
      .filter(Boolean);
  }, [previousServices]);

  useEffect(() => {
    let cancelled = false;
    const faceUserId = user?.id;
    if (!faceUserId) return undefined;
    (async () => {
      try {
        const base = (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
        if (!base) throw new Error('no api');
        const headers = franchiseAuthHeaders({ Accept: 'application/json' });
        const res = await fetch(`${base}/register-services/history/${encodeURIComponent(faceUserId)}`, {
          headers,
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(data.items) && data.items.length) {
          if (!cancelled) setPreviousServices(data.items);
          return;
        }
        throw new Error(data.error || 'empty');
      } catch (_) {
        const visits = Array.isArray(profile?.history) ? profile.history : [];
        const fromVisits = [];
        visits.forEach((visit) => {
          const summaryPayload = visit?.services_summary;
          const selected =
            (summaryPayload && summaryPayload.selected_services) ||
            (summaryPayload && summaryPayload.services) ||
            [];
          (Array.isArray(selected) ? selected : []).forEach((item) => {
            const name = typeof item === 'string' ? item : item?.name;
            if (!name) return;
            fromVisits.push({
              id: `${visit.id || visit.visited_at}-${name}`,
              service_name: name,
              selected_at: visit.visited_at,
            });
          });
        });
        if (!cancelled) setPreviousServices(fromVisits);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, profile?.history]);

  const guestUser = liveUser || user;
  const displayName = guestUser?.name || guestUser?.full_name || 'there';
  const initial = String(displayName).trim().charAt(0).toUpperCase() || 'N';
  const visitCount = Number(summary.visit_count ?? 0);
  const greetingTitle = isNewMember ? 'Welcome,' : 'Welcome back,';
  const sideTagline = isNewMember ? "We're delighted to meet you" : "We're glad to see you again";
  const passportDuplicateNotice = useMemo(() => {
    if (!isPassport || !passportDuplicateVisit) return '';
    const row = location.state?.checkin || null;
    return passportDuplicateVisitMessage(row || { status: 'active' });
  }, [isPassport, passportDuplicateVisit, location.state?.checkin]);

  const continueAfterCheckIn = async () => {
    const nextPath = location.state?.next || null;
    const isSalonEntry =
      location.state?.mode === 'salon' ||
      (typeof nextPath === 'string' && nextPath.startsWith('/naturals/salon'));

    if (isContinuing) return;
    setIsContinuing(true);
    setProceedNotice('');
    const guestMeta = isGuestQr ? readGuestFlowAuth() : null;
    try {
    const snapshot = {
      faceUserId: user.id,
      user,
      profile,
      confidence,
      isNewMember,
      captureUrl,
      checkedInAt: new Date().toISOString(),
      selectedServices: [],
      lastSelectedServices: lastTakenServices,
      fromMobileLookup: location.state?.fromMobileLookup || undefined,
    };
    persistFaceProfileSnapshot(snapshot);
    const mobile = user.mobile || user.phone || '';
    const display = user.name || user.full_name || '';
    const faceOrg =
      user.organization_id ||
      user.organizationId ||
      resolveFaceOrganizationId() ||
      getFranchiseOrganizationId() ||
      '';
    const visitOrg = resolveFaceVisitOrganizationId() || '';
    const faceDob =
      preferences.date_of_birth ||
      preferences.dob ||
      user?.date_of_birth ||
      user?.dob ||
      '';
    const faceAge = user?.age != null && String(user.age).trim() !== '' ? user.age : '';
    const visitReference = isSalonEntry
      ? buildSalonVisitReference(user.id, token)
      : null;
    const entrySource = isSalonEntry
      ? location.state?.fromMobileLookup
        ? 'salon_mobile'
        : 'salon_face'
      : isGuestPhone
        ? 'face_identity_qr'
        : 'face_identity';

    if (isSalonEntry) {
      markSalonFaceCheckedIn({
        faceUserId: user.id,
        comparePrevious: !isNewMember,
        mobile,
        name: display,
        email: user.email || '',
        gender: user.gender || '',
        organizationId: faceOrg,
        dob: faceDob,
        age: faceAge,
        visitReference: visitReference || undefined,
        entrySource,
      });
      navigate(SALON_GUEST_INTENT_PATH, {
        replace: true,
        state: {
          faceUserId: user.id,
          faceProfile: snapshot,
          token,
          deviceInfo,
          comparePrevious: !isNewMember,
          visitReference: visitReference || undefined,
          entrySource,
          organizationId: faceOrg || undefined,
        },
      });
    }

    let partnerQueueWarning = '';
    try {
      await checkinFaceUser(user.id, {
        notes: isNewMember ? 'welcome_new' : 'welcome_return',
        services_summary: {
          selected_services: [],
          service_names: [],
          last_selected_services: lastTakenServices,
        },
        organization_id: faceOrg,
        visit_organization_id: visitOrg || undefined,
      });
    } catch (_) {
      /* non-blocking */
    }

    let checkinRecord = null;
    if (!isSalonEntry) {
      try {
        const { createPartnerCheckinQueue } = await import(
          '../../salon-admin/utils/checkinQueueApi'
        );
        const faceThumb =
          (captureUrl && (await compressFaceThumbnail(captureUrl))) || captureUrl || '';
        const guestLobbyAt = isGuestQr ? new Date().toISOString() : '';
        const qrSource = isGuestQr ? 'guest_qr' : entrySource;
        checkinRecord = await createPartnerCheckinQueue({
          faceUserId: user.id,
          customerName: display,
          mobile,
          selectedServices: [],
          organizationId: faceOrg,
          source: qrSource,
          startService: false,
          referenceToken: visitReference || undefined,
          customerSnapshot: {
            name: display,
            full_name: display,
            mobile,
            email: user.email || '',
            gender: user.gender || '',
            age: faceAge,
            date_of_birth: faceDob,
            face_user_id: user.id,
            organization_id: faceOrg,
            reference_token: visitReference || undefined,
            last_salon_reference: user.last_salon_reference || user.lastSalonReference || '',
            profile,
            selected_services: [],
            last_selected_services: lastTakenServices,
            is_new_member: isNewMember,
            face_image: faceThumb || undefined,
            captureUrl: faceThumb || undefined,
            ...(isGuestQr
              ? {
                  guest_confirmed_at: guestLobbyAt,
                  visit_intent: VISIT_INTENT_SERVICE,
                  entry_source: 'guest_qr',
                }
              : {}),
          },
        });
        if (isGuestQr && checkinRecord?.id) {
          handoffFromCheckin(checkinRecord);
          setActiveCheckinSession({
            id: checkinRecord.id,
            code: checkinRecord.customer_code,
            status: checkinRecord.status || 'active',
            faceUserId: user.id,
          });
          try {
            window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        partnerQueueWarning =
          err?.message || 'Could not notify the partner check-in board. Your visit was still recorded.';
      }
    }

    if (!isSalonEntry && isGuestQr) {
      setCompleteModal({
        open: true,
        guestName: display || 'Guest',
        isNewMember,
        customerCode: checkinRecord?.customer_code || '',
        services: lastTakenServices,
        warning: partnerQueueWarning,
        franchiseName: guestMeta?.franchise_name || '',
      });
      return;
    }

    if (!isSalonEntry) {
      setProceedNotice(
        partnerQueueWarning
          ? `Checked in. ${partnerQueueWarning}`
          : 'Checked in — thank you!',
      );
      await new Promise((r) => setTimeout(r, partnerQueueWarning ? 1800 : 900));
      navigate(homePath, { replace: true });
      return;
    }

    // Salon entry already navigated to visit-intent — optional guest-session link only.
    try {
      const adminBase = (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
      if (adminBase) {
        const headers = franchiseAuthHeaders({ 'Content-Type': 'application/json' });
        await fetch(`${adminBase}/naturals/guest-session-link`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            faceUserId: user.id,
            mobile: mobile || undefined,
            organizationId: faceOrg || undefined,
            salonReference: visitReference || undefined,
            previousSalonReference:
              user.last_salon_reference || user.lastSalonReference || undefined,
          }),
        });
      }
    } catch (_) {
      /* non-blocking */
    }
    } finally {
      setIsContinuing(false);
    }
  };

  const locationBlock = (
    <article className="fi-welcome__tile fi-welcome__tile--location">
      <div className="fi-welcome__tile-head">
        <span className="fi-welcome__tile-icon">
          <IconPin />
        </span>
        <h3>Last visit location</h3>
      </div>
      {hasLastVisitLocation ? (
        <div className="fi-welcome__location-fields">
          {lastVisitFranchiseName ? (
            <div className="fi-welcome__kv">
              <span className="fi-welcome__kv-key">Salon</span>
              <span className="fi-welcome__kv-val fi-welcome__strong">{lastVisitFranchiseName}</span>
            </div>
          ) : null}
          <div className="fi-welcome__kv">
            <span className="fi-welcome__kv-key">Area</span>
            <span className="fi-welcome__kv-val">{lastVisitArea || '—'}</span>
          </div>
          <div className="fi-welcome__kv">
            <span className="fi-welcome__kv-key">City</span>
            <span className="fi-welcome__kv-val">{lastVisitCity || '—'}</span>
          </div>
          {lastVisitState ? (
            <div className="fi-welcome__kv">
              <span className="fi-welcome__kv-key">State</span>
              <span className="fi-welcome__kv-val">{lastVisitState}</span>
            </div>
          ) : null}
          <div className="fi-welcome__kv">
            <span className="fi-welcome__kv-key">Country</span>
            <span className="fi-welcome__kv-val">{lastVisitCountry || '—'}</span>
          </div>
          {lastVisitAddress && !lastVisitArea && !lastVisitCity ? (
            <div className="fi-welcome__kv">
              <span className="fi-welcome__kv-key">Address</span>
              <span className="fi-welcome__kv-val">{lastVisitAddress}</span>
            </div>
          ) : null}
        </div>
      ) : (
        <p>No franchise location recorded yet</p>
      )}
    </article>
  );

  const historyBlock = (
    <article className="fi-welcome__tile fi-welcome__history">
      <div className="fi-welcome__tile-head">
        <span className="fi-welcome__tile-icon">
          <IconCalendar />
        </span>
        <h3>Visit history</h3>
      </div>
      <div className="fi-welcome__history-body">
        <div className="fi-welcome__history-copy">
          {lastVisit ? (
            <p>Last visit: {formatVisitAt(lastVisit.visited_at)}</p>
          ) : (
            <p>First check-in</p>
          )}
          {hasLastVisitLocation ? (
            <p className="fi-welcome__history-location">
              {[lastVisitArea, lastVisitCity, lastVisitState, lastVisitCountry]
                .filter(Boolean)
                .join(', ') || lastVisitFranchiseName || lastVisitAddress}
            </p>
          ) : null}
        </div>
        <div className="fi-welcome__visits-ring" aria-hidden="true">
          <strong>{visitCount}</strong>
          <span>Total Visits</span>
        </div>
      </div>
    </article>
  );

  const previousServicesRecent = useMemo(
    () => recentPreviousServices(previousServices, 8),
    [previousServices],
  );

  const previousServicesBlock = (
    <article className="fi-welcome__tile fi-welcome__prev-services">
      <div className="fi-welcome__tile-head">
        <span className="fi-welcome__tile-icon">
          <IconClock />
        </span>
        <h3>Previous services</h3>
      </div>
      {previousServicesRecent.length ? (
        <ul className="fi-welcome__prev-list">
          {previousServicesRecent.map((item) => (
            <li key={item.id}>
              <strong>{item.service_name}</strong>
              <span className="fi-welcome__prev-meta">
                {item.selected_at && typeof item.selected_at === 'string'
                  ? item.selected_at.slice(0, 10)
                  : ''}
                <span aria-hidden="true">›</span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p>No previous services yet</p>
      )}
    </article>
  );

  const nextPath = location.state?.next || null;
  const isSalonEntry =
    location.state?.mode === 'salon' ||
    (typeof nextPath === 'string' && nextPath.startsWith('/naturals/salon'));

  const actions = isPassport ? null : (
    <div className="fi-welcome__actions">
      <button
        type="button"
        className="fi-welcome__btn fi-welcome__btn--primary"
        onClick={continueAfterCheckIn}
        disabled={isContinuing || completeModal.open}
      >
        {isContinuing ? 'Saving…' : 'Proceed'}
        <span aria-hidden="true">→</span>
      </button>
      {!isGuestQr ? (
        <button
          type="button"
          className="fi-welcome__btn fi-welcome__btn--ghost fi-welcome__btn--scan-again"
          onClick={() =>
            navigate(faceIdentityRoute('scan', location.pathname), {
              replace: true,
              state: buildKioskNavState(location.state, {
                token,
                deviceInfo,
                next: nextPath,
                mode: location.state?.mode || (isSalonEntry ? 'salon' : 'frontdesk'),
              }),
            })
          }
        >
          <IconClose />
          Scan again
        </button>
      ) : null}
    </div>
  );

  if (isPassport && passportKioskDismissed) {
    return (
      <>
        <div
          className={`fi-welcome fi-scroll-page fi-welcome--passport${isGuestPhone ? ' fi-welcome--guest' : ''}`}
        >
          <main className="fi-welcome__empty">
            <FaceIdentityBrandLogo
              showLogo={showLogo}
              orgLogoUrl={orgLogoUrl}
              onLogoError={onLogoError}
              imgClassName="fi-welcome__logo"
              fallbackClassName="fi-welcome__logo-text"
            />
            <h1 className="fi-welcome__greeting-title">Thank you</h1>
            <p className="fi-welcome__greeting-sub">
              {isGuestPhone
                ? 'Your check-in is complete. Please see the front desk if you need anything.'
                : 'Welcome — thank you for being here today.'}
            </p>
          </main>
        </div>
        <GuestCheckInCompleteModal
          open={completeModal.open}
          guestName={completeModal.guestName}
          isNewMember={completeModal.isNewMember}
          customerCode={completeModal.customerCode}
          services={completeModal.services}
          warning={completeModal.warning}
          franchiseName={completeModal.franchiseName}
          onClosed={() => {
            setCompleteModal((prev) => ({ ...prev, open: false }));
            if (isGuestPhone) clearGuestFlowAuth();
          }}
        />
      </>
    );
  }

  const confirmCandidates = location.state?.confirmCandidates || pending?.confirmCandidates || null;

  if (Array.isArray(confirmCandidates) && confirmCandidates.length > 0 && !user) {
    return (
      <div
        className={`fi-welcome fi-scroll-page${isPassport ? ' fi-welcome--passport' : ''}${isGuestPhone ? ' fi-welcome--guest' : ''}`}
      >
        <main className="fi-welcome__empty fi-welcome__empty--confirm">
          <ConfirmFaceMatchPicker
            candidates={confirmCandidates}
            message={location.state?.confirmMessage || pending?.confirmMessage}
            captureUrl={captureUrl}
            onConfirmed={async (snapshot) => {
              let passportCheckinId = location.state?.checkinId || null;
              let passportDuplicateVisitConfirm = false;
              let passportCheckinRow = null;
              if (isPassport) {
                try {
                  if (isPassportKiosk) await disablePassportKioskScanAfterCapture();
                  const queued = await submitPassportReturningCheckin({
                    user: snapshot.user,
                    profile: snapshot.profile,
                    captureUrl: snapshot.captureUrl,
                    isNewMember: false,
                  });
                  passportCheckinRow = queued?.checkin || null;
                  passportCheckinId = passportCheckinRow?.id ?? passportCheckinId;
                  passportDuplicateVisitConfirm = Boolean(queued?.reused);
                } catch (queueErr) {
                  throw queueErr;
                }
              }
              navigate(faceIdentityRoute('welcome', location.pathname), {
                replace: true,
                state: {
                  ...location.state,
                  user: snapshot.user,
                  profile: snapshot.profile,
                  confidence: snapshot.confidence,
                  captureUrl: snapshot.captureUrl,
                  confirmCandidates: undefined,
                  confirmMessage: undefined,
                  passportGuestShown: isPassport ? true : undefined,
                  checkinId: passportCheckinId || undefined,
                  passportDuplicateVisit: passportDuplicateVisitConfirm || undefined,
                  checkin:
                    passportDuplicateVisitConfirm && passportCheckinRow
                      ? passportCheckinRow
                      : undefined,
                },
              });
            }}
            onScanAgain={() =>
              navigate(faceIdentityRoute('scan', location.pathname), {
                replace: true,
                state: buildKioskNavState(location.state, {
                  token,
                  deviceInfo,
                  next: location.state?.next || null,
                  mode: location.state?.mode || null,
                  confirmCandidates: undefined,
                }),
              })
            }
            onUseMobile={() =>
              navigate(faceIdentityRoute('mobile', location.pathname), {
                replace: true,
                state: buildKioskNavState(location.state, {
                  token,
                  deviceInfo,
                  next: location.state?.next || null,
                  mode: location.state?.mode || null,
                  fromFaceUnknown: true,
                  captureUrl,
                  confirmCandidates: undefined,
                  confirmMessage: undefined,
                }),
              })
            }
          />
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        className={`fi-welcome fi-scroll-page${isPassport ? ' fi-welcome--passport' : ''}${isGuestPhone ? ' fi-welcome--guest' : ''}`}
      >
        <main className="fi-welcome__empty">
          <h1>No check-in found</h1>
          <p>{isPassport ? 'Please ask the front desk for assistance.' : 'Please scan your face again.'}</p>
          {!isPassport ? (
            <button
              type="button"
              className="fi-welcome__btn fi-welcome__btn--primary"
              onClick={() =>
                navigate(faceIdentityRoute('scan', location.pathname), {
                  replace: true,
                  state: buildKioskNavState(location.state, {
                    token,
                    deviceInfo,
                    next: nextPath,
                    mode: location.state?.mode || (isSalonEntry ? 'salon' : 'frontdesk'),
                  }),
                })
              }
            >
              Back to scan
            </button>
          ) : null}
        </main>
      </div>
    );
  }

  return (
    <div
      className={`fi-welcome fi-scroll-page${isPassport ? ' fi-welcome--passport' : ''}${isGuestPhone ? ' fi-welcome--guest' : ''}`}
    >
      <div className="fi-welcome__stage-shell">
        <header className="fi-welcome__top">
          <div className="fi-welcome__top-row">
            <div className="fi-welcome__brand">
              <FaceIdentityBrandLogo
                showLogo={showLogo}
                orgLogoUrl={orgLogoUrl}
                onLogoError={onLogoError}
                imgClassName="fi-welcome__logo"
                fallbackClassName="fi-welcome__logo-text"
              />
            </div>
            {isPassport ? (
              <FaceIdentityFlowNav
                showBack={false}
                showHome={presentationFromHub}
                homePath={homePath}
                showLogout={false}
                showRefresh
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <FaceIdentityFlowNav
                onBack={() =>
                  navigate(
                    location.state?.fromMobileLookup
                      ? faceIdentityRoute('mobile', location.pathname)
                      : faceIdentityRoute('scan', location.pathname),
                    {
                      replace: true,
                      state: buildKioskNavState(location.state, {
                        token,
                        deviceInfo,
                        next: location.state?.next || null,
                        mode: location.state?.mode || null,
                      }),
                    },
                  )
                }
                homePath={homePath}
              />
            )}
          </div>
          <div className="fi-welcome__clock" aria-live="polite">
            <IconCalendar />
            <span>{clockLabel}</span>
          </div>
        </header>

        <section className="fi-welcome__greeting" aria-label="Welcome greeting">
          <h1 className="fi-welcome__greeting-title">
            {greetingTitle} <em>{displayName}</em>
          </h1>
          <p className="fi-welcome__greeting-sub">{sideTagline}</p>
          {passportDuplicateNotice ? (
            <p className="fi-welcome__duplicate-banner" role="status">
              {passportDuplicateNotice}
            </p>
          ) : null}
        </section>

        <main className="fi-welcome__stage">
          <aside className="fi-welcome__portrait">
            <div className="fi-welcome__photo">
              {captureUrl ? (
                <img src={captureUrl} alt={displayName} />
              ) : (
                <div className="fi-welcome__photo-fallback" aria-hidden="true">
                  {initial}
                </div>
              )}
            </div>
          </aside>

          <div className="fi-welcome__info-grid">
            {locationBlock}
            {historyBlock}
          </div>

          <div className="fi-welcome__main-stack">
            {proceedNotice ? <p className="fi-welcome__notice" role="status">{proceedNotice}</p> : null}

            {previousServicesBlock}
            {actions}
          </div>
        </main>
      </div>

      <GuestCheckInCompleteModal
        open={completeModal.open}
        guestName={completeModal.guestName}
        isNewMember={completeModal.isNewMember}
        customerCode={completeModal.customerCode}
        services={completeModal.services}
        warning={completeModal.warning}
        franchiseName={completeModal.franchiseName}
        onClosed={() => {
          setCompleteModal((prev) => ({ ...prev, open: false }));
          if (isGuestPhone) clearGuestFlowAuth();
        }}
      />
    </div>
  );
};

export default FaceIdentityWelcomePage;
